// src/components/modals/GameModals.tsx
import React from "react";
import { Piece, Position } from "../../types/Board.ts";
import { PIECE_DEFINITIONS } from "../../data/pieceDefinitions.ts";

// ============================================================================
// FIFTY MOVE WARNING MODAL
// ============================================================================

interface FiftyMoveWarningModalProps {
  show: boolean;
  fiftyMoveLimit: number;
  seventyFiveMoveLimit: number;
  halfmoveClock: number;
  onClaimDraw: () => void;
  onContinue: () => void;
}

export const FiftyMoveWarningModal: React.FC<FiftyMoveWarningModalProps> = ({
  show,
  fiftyMoveLimit,
  seventyFiveMoveLimit,
  halfmoveClock,
  onClaimDraw,
  onContinue,
}) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "30px",
          maxWidth: "500px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
        }}
      >
        <h2 style={{ marginTop: 0, color: "#333", textAlign: "center" }}>
          50-Move Rule
        </h2>
        <p
          style={{
            fontSize: "16px",
            color: "#666",
            textAlign: "center",
            marginBottom: "15px",
          }}
        >
          No capture or pawn move has been made in the last {fiftyMoveLimit}{" "}
          consecutive moves by each player.
        </p>
        <p
          style={{
            fontSize: "16px",
            color: "#666",
            textAlign: "center",
            marginBottom: "15px",
          }}
        >
          <strong>Halfmove Clock: {halfmoveClock}</strong>
        </p>
        <p
          style={{
            fontSize: "14px",
            color: "#666",
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          You may claim a draw now, or continue playing. The game will
          automatically be drawn at {seventyFiveMoveLimit} moves (
          {seventyFiveMoveLimit * 2} half-moves).
        </p>
        <div
          style={{ display: "flex", gap: "10px", justifyContent: "center" }}
        >
          <button
            onClick={onClaimDraw}
            style={{
              padding: "10px 20px",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Claim Draw
          </button>
          <button
            onClick={onContinue}
            style={{
              padding: "10px 20px",
              backgroundColor: "#ff9800",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Continue Playing
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CLOCK WARNING MODAL
// ============================================================================

interface ClockWarningModalProps {
  show: boolean;
  onStartClock: () => void;
  onContinue: () => void;
}

export const ClockWarningModal: React.FC<ClockWarningModalProps> = ({
  show,
  onStartClock,
  onContinue,
}) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
      onClick={onContinue}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "30px",
          maxWidth: "500px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0, color: "#333", textAlign: "center" }}>
          ⚠️ Clock Not Started
        </h2>
        <p
          style={{
            fontSize: "16px",
            color: "#666",
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          The game clock is enabled but has not been started yet. Would you
          like to start it now?
        </p>
        <div
          style={{ display: "flex", gap: "10px", justifyContent: "center" }}
        >
          <button
            onClick={onStartClock}
            style={{
              padding: "10px 20px",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Start Clock
          </button>
          <button
            onClick={onContinue}
            style={{
              padding: "10px 20px",
              backgroundColor: "#ff9800",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Continue Without Clock
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// TIMEOUT MODAL
// ============================================================================

interface TimeoutResult {
  winner: string;
  loser: string;
  result: "win" | "draw";
  opponentMaterial: string;
}

interface TimeoutModalProps {
  show: boolean;
  timeoutResult: TimeoutResult | null;
  boardRows: number;
  onClose: () => void;
}

export const TimeoutModal: React.FC<TimeoutModalProps> = ({
  show,
  timeoutResult,
  boardRows,
  onClose,
}) => {
  if (!show || !timeoutResult) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "30px",
          maxWidth: "600px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
        }}
      >
        <h2
          style={{
            marginTop: 0,
            color: timeoutResult.result === "win" ? "#4CAF50" : "#ff9800",
            textAlign: "center",
          }}
        >
          ⏰ Time's Up!
        </h2>
        <p
          style={{
            fontSize: "18px",
            color: "#333",
            textAlign: "center",
            marginBottom: "15px",
            fontWeight: "bold",
          }}
        >
          {timeoutResult.loser.charAt(0).toUpperCase() +
            timeoutResult.loser.slice(1)}{" "}
          ran out of time!
        </p>

        {timeoutResult.result === "win" ? (
          <>
            <p
              style={{
                fontSize: "16px",
                color: "#666",
                textAlign: "center",
                marginBottom: "10px",
              }}
            >
              {timeoutResult.winner.charAt(0).toUpperCase() +
                timeoutResult.winner.slice(1)}{" "}
              wins by timeout!
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              {timeoutResult.loser.charAt(0).toUpperCase() +
                timeoutResult.loser.slice(1)}{" "}
              had sufficient mating material.
            </p>
          </>
        ) : (
          <>
            <p
              style={{
                fontSize: "16px",
                color: "#666",
                textAlign: "center",
                marginBottom: "10px",
              }}
            >
              The game is a draw!
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                textAlign: "center",
                marginBottom: "15px",
              }}
            >
              {timeoutResult.winner.charAt(0).toUpperCase() +
                timeoutResult.winner.slice(1)}{" "}
              has insufficient mating material to win.
            </p>

            <h3
              style={{
                fontSize: "16px",
                color: "#333",
                textAlign: "center",
                marginTop: "20px",
                marginBottom: "10px",
              }}
            >
              Timeout Material Rules (8×8 - 11×11):
            </h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: "20px",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f5f5f5" }}>
                  <th
                    style={{
                      border: "1px solid #ddd",
                      padding: "8px",
                      textAlign: "left",
                    }}
                  >
                    Material
                  </th>
                  <th
                    style={{
                      border: "1px solid #ddd",
                      padding: "8px",
                      textAlign: "left",
                    }}
                  >
                    Result
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  style={{
                    backgroundColor:
                      timeoutResult.opponentMaterial === "K only"
                        ? "#fff9c4"
                        : "white",
                  }}
                >
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    K only
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    Draw
                  </td>
                </tr>
                <tr
                  style={{
                    backgroundColor:
                      timeoutResult.opponentMaterial === "K+B"
                        ? "#fff9c4"
                        : "white",
                  }}
                >
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    K+B
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    Draw
                  </td>
                </tr>
                <tr
                  style={{
                    backgroundColor:
                      timeoutResult.opponentMaterial === "K+N"
                        ? "#fff9c4"
                        : "white",
                  }}
                >
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    K+N
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    Draw
                  </td>
                </tr>
                <tr
                  style={{
                    backgroundColor:
                      timeoutResult.opponentMaterial === "K+N+N"
                        ? "#fff9c4"
                        : "white",
                  }}
                >
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    K+N+N
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    Draw
                  </td>
                </tr>
                <tr
                  style={{
                    backgroundColor:
                      timeoutResult.opponentMaterial ===
                      "K+B+B (opposite colors)"
                        ? "#fff9c4"
                        : "white",
                  }}
                >
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    K+B+B (opposite colors)
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    Draw
                  </td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    K+B+N
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    Loss
                  </td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    K+Q
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    Loss
                  </td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    K+R
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    Loss
                  </td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    K+Pawn(s)
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    Loss
                  </td>
                </tr>
                <tr
                  style={{
                    backgroundColor:
                      (boardRows >= 10 &&
                        timeoutResult.opponentMaterial ===
                          "Any other combination of three or more pieces") ||
                      (boardRows < 10 &&
                        timeoutResult.opponentMaterial ===
                          "Any combination with Q/R/Pawn")
                        ? "#fff9c4"
                        : "white",
                  }}
                >
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    {boardRows >= 10
                      ? "Any other combination of three or more pieces"
                      : "Any combination with Q/R/Pawn"}
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                    Loss
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PROMOTION DIALOG
// ============================================================================

interface PromotionDialogProps {
  show: boolean;
  availablePromotionPieces: string[];
  promotionMove: {
    from: Position;
    to: Position;
    capturedPiece?: Piece;
  } | null;
  boardRows: number;
  pieces: Map<string, Piece>;
  getPieceAt: (row: number, col: number) => Piece | undefined;
  onPromotion: (pieceType: string) => void;
}

export const PromotionDialog: React.FC<PromotionDialogProps> = ({
  show,
  availablePromotionPieces,
  promotionMove,
  boardRows,
  pieces,
  getPieceAt,
  onPromotion,
}) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "30px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
        }}
      >
        <h2 style={{ marginTop: 0, color: "#333", textAlign: "center" }}>
          Promote{" "}
          {promotionMove &&
            getPieceAt(promotionMove.from.row, promotionMove.from.col)
              ?.type.charAt(0)
              .toUpperCase()}
          {promotionMove &&
            getPieceAt(
              promotionMove.from.row,
              promotionMove.from.col
            )?.type.slice(1)}
        </h2>
        <p
          style={{
            textAlign: "center",
            color: "#666",
            marginBottom: "20px",
          }}
        >
          Choose a piece to promote to:
        </p>
        <div
          style={{
            display: "flex",
            gap: "15px",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {availablePromotionPieces.map((pieceType) => {
            const piece = getPieceAt(
              promotionMove?.from.row || 0,
              promotionMove?.from.col || 0
            );
            const pieceDefinition = PIECE_DEFINITIONS[pieceType];
            if (!pieceDefinition) return null;

            const abbreviation = pieceDefinition.abbreviation;
            const boardSizeStr = `${boardRows}×${boardRows}`;

            let pieceValue = 0;
            try {
              const pieceValuesModule = require("../../data/pieceValues.ts");
              const pieceValues =
                pieceValuesModule.PIECE_VALUES[abbreviation.toUpperCase()];
              if (pieceValues) {
                const valueEntry = pieceValues.find(
                  (v: any) => v.boardSize === boardSizeStr
                );
                if (valueEntry) {
                  pieceValue = valueEntry.value;
                }
              }
            } catch (error) {
              console.warn(`Could not get value for: ${pieceType}`);
            }

            return (
              <div
                key={pieceType}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  onClick={() => onPromotion(pieceType)}
                  style={{
                    width: "80px",
                    height: "80px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#8b7355",
                    borderRadius: "8px",
                    cursor: "pointer",
                    border: "3px solid #5d4a37",
                    transition: "all 0.2s",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#a0886d";
                    e.currentTarget.style.borderColor = "#6b5645";
                    e.currentTarget.style.transform = "scale(1.1)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 8px rgba(0, 0, 0, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#8b7355";
                    e.currentTarget.style.borderColor = "#5d4a37";
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 4px rgba(0, 0, 0, 0.2)";
                  }}
                >
                  <img
                    src={`/pieces/${abbreviation.toLowerCase()}.png`}
                    alt={pieceType}
                    style={{
                      width: "60px",
                      height: "60px",
                      filter:
                        piece?.color === "white"
                          ? "brightness(0) invert(1) drop-shadow(0 0 2px rgba(0, 0, 0, 0.8))"
                          : "brightness(0) drop-shadow(0 0 2px rgba(255, 255, 255, 0.5))",
                    }}
                  />
                </div>
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {pieceType.charAt(0).toUpperCase() + pieceType.slice(1)}
                </div>
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  Value: {pieceValue.toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
